<template>
    <mc-form-item
        :required="attr.required"
        :label="attr.attrName"
    >
        <!-- 产地（1200000094）和品牌（1200000088）扩展属性需要特殊处理-->
        <SgAttrSelect
            v-if="[1200000088,1200000094].includes(attr.attrId)"
            v-model="attr.selectedValues"
            :attr-id="attr.attrId"
            :disabled="disabled"
        />
        <components
            :is="compInfo.name"
            v-else
            v-model="innerValue"
            v-bind="compInfo.attrs"
            @change="((val)=>{handleChange(val,attr.attrName)})"
        />
    </mc-form-item>
</template>

<script>
import { computed, getCurrentInstance } from '@vue/composition-api';
import SgAttrSelect from './SgAttrSelect';

export default {
    name: 'ExtAttrItem',

    components: {
        SgAttrSelect,
    },

    props: {
        attr: {
            type: Object,
            default: () => ({}),
        },
        mode: {
            type: String,
            default: 'edit',
        },
        inputTypeIsTextarea: {
            type: Boolean,
            deafault: false,
        },
        clearable: {
            type: Boolean,
            default: true,
        },
        disabled: { // 是否禁用
            type: Boolean,
            default: false,
        },
        showErrorMsg: {
            type: Boolean,
            default: true,
        },
    },

    setup() {
        const { proxy, props } = getCurrentInstance();
        const TextMaxLength = 300;
        const ExtType = {
            /* input type=number */
            NUMBER: 1,
            /* input type=text */
            TEXT: 2,
            /* input type=radio */
            RADIO: 3,
            /* input type=checkbox */
            MULTI: 4,
        };

        // 将选中值的数组转换为对应的值 selectValues => value
        const svToVal = (sv, vType) => {
            let v = '';
            switch (vType) {
                case ExtType.MULTI: // 如果类型为多选
                    v = sv.map((e) => e.id); // 返回选中值的 id 数组
                    break;
                case ExtType.RADIO: // 如果类型为单选
                    v = sv[0] ? sv[0].id : ''; // 返回选中值的 id，如果没有选中值，则返回空字符串
                    break;
                case ExtType.NUMBER: // 如果类型为数字
                case ExtType.TEXT: // 如果类型为文本
                    v = (sv[0] && sv[0].value) || ''; // 返回选中值的 value，如果没有选中值，则返回空字符串
                    break;
                default:
                    break;
            }
            return v;
        };

        // 将值转换为选中值的数组 value => selectValues
        const vToSv = (v, vType) => {
            // 获取属性的选中值，如果没有则默认为空数组
            const { selectValues = [] } = props.attr;
            // 初始化选中值数组
            const sv = [];
            // 如果值的类型是多选或单选
            if ([ExtType.MULTI, ExtType.RADIO].includes(vType)) {
                // 如果值是数组则直接使用，否则将其转换为数组
                const values = Array.isArray(v) ? v : [v];
                // 遍历值数组
                values.forEach((e) => {
                    // 在选中值中查找与当前值相等的项
                    const attr = selectValues.find((item) => +item.id === +e);
                    // 获取找到的项的值，如果没有找到则默认为空字符串
                    const attrValue = (attr && attr.value) || '';
                    // 如果当前值是字符串或找到的项有值
                    if (typeof (e) === 'string' || attrValue) {
                        // 将当前值和找到的项的值或当前值添加到选中值数组中
                        sv.push({ id: e, value: attrValue || e });
                    }
                });
                // 如果值的类型是数字或文本
            } else if ([ExtType.NUMBER, ExtType.TEXT].includes(vType)) {
                // 如果值不为空字符串
                if (v.trim()) {
                    // 将值添加到选中值数组中，id 默认为 0
                    sv.push({ id: 0, value: v.trim() });
                }
            }
            // 返回选中值数组
            return sv;
        };

        // 定义一个验证数字的方法
        const validateNumber = (val, attrName) => {
            // 使用正则表达式验证值是否为数字，且最多支持2位小数
            if (!/(^-?[1-9]\d*(\.\d{1,2})?$)|(^-?0(\.\d{1,2})?$)/.test(val)) {
                // 如果验证失败，返回错误信息
                return `${attrName}属性必须为数字且最多支持2位小数`;
            }
            // 如果验证成功，返回空字符串
            return '';
        };

        // 定义一个验证文本的方法
        const validateText = (val, attrName) => {
            // 验证文本的长度是否超过最大长度
            if (val.length > TextMaxLength) {
                // 如果验证失败，返回错误信息
                return `${attrName}属性长度必须小于${TextMaxLength}`;
            }
            // 如果验证成功，返回空字符串
            return '';
        };

        // 验证方法，用于验证输入的值是否符合规则
        // eslint-disable-next-line no-use-before-define
        const validate = (val = innerValue.value) => {
            // 初始化错误信息为空字符串
            let errorMsg = '';
            // 从属性中解构出值类型、是否必填和属性名
            const { valueType, required, attrName } = props.attr;
            // 如果值是字符串，则去除两端的空白字符
            if (typeof val === 'string') val = val.trim();
            // 如果属性是必填的，并且值为空或长度为0，则设置错误信息为必填项错误
            if (required && (val === '' || val.length === 0)) {
                errorMsg = `${attrName}属性为必填项`;
            } else if (val) { // 如果值不为空
                // 如果值类型是数字，则调用 validateNumber 方法进行验证，并获取错误信息
                if (valueType === ExtType.NUMBER) {
                    errorMsg = validateNumber(val, attrName);
                }
                // 如果值类型是文本，则调用 validateText 方法进行验证，并获取错误信息
                if (valueType === ExtType.TEXT) {
                    errorMsg = validateText(val, attrName);
                }
            } else { // 如果值为空，则错误信息为空字符串
                errorMsg = '';
            }
            // 如果有错误信息，并且允许显示错误信息，则使用代理对象 proxy 的 $mc.message.error 方法显示错误信息
            if (errorMsg && props.showErrorMsg) {
                proxy.$mc.message.error(errorMsg);
            }
            // 返回错误信息
            return errorMsg;
        };

        // 触发值改变事件
        const emitValue = (val) => {
            // 从 props 中解构出 attr 属性
            const { attr } = props;
            // 调用 vToSv 方法将值转换为选中值的数组
            const sv = vToSv(val, attr.valueType);
            // 将转换后的选中值数组赋值给 attr 的 selectedValues 属性
            attr.selectedValues = sv;
            // 使用代理对象 proxy 触发 'change-ext-attr' 事件，并传递包含 attrId 和 selectedValues 的对象作为参数
            proxy.$emit('change-ext-attr', {
                attrId: attr.attrId,
                selectedValues: sv,
            });
        };

        // 值改变时的操作
        const onChangeValue = (val) => {
            validate(val);
            emitValue(val);
        };

        const innerValue = computed({
            get() {
                const { attr } = props;
                return svToVal(attr.selectedValues || [], attr.valueType);
            },
            set(val) {
                onChangeValue(val);
            },
        });

        // 计算出有效的选项
        const options = computed(() => {
            const { attr } = props;
            return (attr.selectValues || []).filter((item) => item.valid);
        });

        // 获取组件信息的方法
        const getComponentInfo = (attr, inputTypeIsTextarea, disabled, clearable, option) => {
            // 初始化绑定对象
            const bind = {
                name: '',
                attrs: {},
            };
            // 根据属性值类型选择组件
            switch (attr.valueType) {
                case ExtType.NUMBER: // 数字类型
                    bind.name = 'mc-input'; // 使用 mc-input 组件
                    break;
                case ExtType.TEXT: // 文本类型
                    bind.name = inputTypeIsTextarea ? 'mc-textarea' : 'mc-input'; // 根据是否为文本域选择组件
                    bind.attrs = inputTypeIsTextarea ? { row: 4 } : {}; // 如果是文本域，设置行数为 4
                    break;
                case ExtType.MULTI: // 多选类型
                case ExtType.RADIO: // 单选类型
                    bind.name = 'mc-select'; // 使用 mc-select 组件
                    bind.attrs = {
                        filterable: true, // 可过滤
                        multiple: attr.valueType === ExtType.MULTI, // 根据属性值类型决定是否多选
                        labelKey: 'value', // 标签键
                        valueKey: 'id', // 值键
                        clearable, // 是否可清空
                        source: option, // 选项源
                        allowCreate: props.selectAllowCreate, // 是否允许创建
                        noDataText: props.selectNoDataText, // 无数据文本
                        scrollStep: { size: 20 }, // 滚动步长
                    };
                    break;
                default:
                    break;
            }
            // 设置是否禁用
            bind.attrs.disabled = disabled;
            // 返回绑定对象
            return bind;
        };

        // 获取组件信息
        const compInfo = computed(() => {
            const {
                attr, inputTypeIsTextarea, disabled, clearable,
            } = props;
            return getComponentInfo(attr, inputTypeIsTextarea, disabled, clearable, options.value);
        });

        // 定义 handleChange 方法，用于触发 change 事件，并将 val 和 name 作为参数传递
        const handleChange = (val, name) => {
            proxy.$emit('change', val, name);
        };

        return {
            svToVal,
            vToSv,
            emitValue,
            innerValue,
            validate,
            onChangeValue,
            options,
            compInfo,
            handleChange,
        };
    },
};
</script>
